import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Handshake, Wrench, Target } from "lucide-react";
import { PageHero } from "@/components/sections/PageHero";
import { Marquee } from "@/components/sections/Marquee";
import { Legacy } from "@/components/sections/Legacy";
import { Reveal, SectionHead } from "@/components/motion/Reveal";
import { COMPANY, IMG } from "@/data/site";

const VALUES = [
  { icon: MapPin, title: "Local", desc: "Based in McMinnville, Tennessee, close to the businesses we support." },
  { icon: Handshake, title: "Partnership", desc: "We work with owners to find equipment that fits their location and customers." },
  { icon: Wrench, title: "Hands-on service", desc: "Installation, maintenance and repair from people who know the machines." },
  { icon: Target, title: "Results-focused", desc: "Entertainment that gives customers more reasons to stay and come back." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero testId="about-hero" eyebrow="About Brewer" lines={["Serious About", "Entertainment"]} img={IMG.arcadeVenue} sub="Brewer Amusement Co. Inc. supplies commercial amusement equipment — and the installation, service and support behind it." />
      <section className="py-24 lg:py-36">
        <div className="container-x grid items-center gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <div className="relative h-[420px] overflow-hidden rounded-[28px] border border-white/5 lg:h-[560px]">
              <img src={IMG.clawMoody} alt="Prize machine in a venue" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-950/70 to-transparent" />
              <p className="absolute bottom-6 left-6 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-300">{COMPANY.street} · {COMPANY.city}</p>
            </div>
          </Reveal>
          <div className="lg:col-span-5 lg:col-start-8">
            <SectionHead index="01" eyebrow="Our Story" title={["Equipment that", "earns its floor space."]} className="lg:grid-cols-1" />
            <Reveal delay={0.15}>
              <p className="mt-8 text-base leading-relaxed text-slate-400">For over 65 years, from our home in McMinnville, Tennessee, Brewer Amusement has helped restaurants, bars, bowling centers, convenience stores, arcades and entertainment centers bring in amusement equipment that customers love.</p>
              <p className="mt-5 text-base leading-relaxed text-slate-400">We keep it simple: help you choose the right machines, get them installed, and keep them running — so your business has one more reason for customers to come back.</p>
              <Link to="/contact" className="btn-primary mt-10 h-12 px-6 text-sm" data-testid="about-contact-button">Talk to Brewer <ArrowRight className="btn-arrow h-4 w-4" /></Link>
            </Reveal>
          </div>
        </div>
      </section>
      <Legacy />
      <Marquee />
      <section className="py-24 lg:py-36">
        <div className="container-x">
          <SectionHead index="02" eyebrow="What we stand for" title="Why partner with Brewer" />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="h-full rounded-[24px] border border-white/5 bg-ink-800 p-8 transition-[border-color] duration-500 hover:border-volt/40" data-testid={`about-value-${i + 1}`}>
                  <v.icon className="h-6 w-6 text-volt" />
                  <h3 className="mt-10 font-display text-2xl font-semibold text-white">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
